/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {forwardRef, useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {useIntl, defineMessages, FormattedMessage} from 'react-intl'
import loadable from '@loadable/component'

import {
    Flex,
    Button,
    Skeleton,
    Box,
    VStack,
    useBreakpointValue,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useDerivedProduct} from '../../hooks/use-derived-product'

import {useMiniCartContext} from '../../contexts/mini-cart-context'
import {useAddToCartConfirmation} from '../../contexts/add-to-cart-confirmation-context'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// project components
import ImageGallery from '../../components/image-gallery'
import Breadcrumb from '../../components/breadcrumb'
import Link from '@salesforce/retail-react-app/app/components/link'
import {Skeleton as ImageGallerySkeleton} from '../../components/image-gallery'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'

import {useCustomToast} from '../../contexts/custom-toast-context'
import {useInView} from '../../hooks/use-in-view'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {getPriceData} from '../../utils/product-utils'
import {
    hasAllValidVariationSelections,
    isVariationAttributeSelectionValid
} from '../../utils/variation-selection'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import StickyButton from './partials/StickyButton'
import Loyalty from './partials/Loyalty'
import ActualFitMessage from '../actual-fit-message'
import {KlarnaScript} from '../../utils/klarna-script'
import GiftCardForm from './partials/giftCardForm'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

// Lazy load modal/drawer components - only loaded when user interacts
// These are not needed for initial render and can be code-split
const NotifyMeModal = loadable(() => import('../notify-me-modal'), {ssr: false})
const ShopSimilarModal = loadable(() => import('../shop-similar-modal'), {ssr: false})
const SizeChartDrawer = loadable(() => import('../size-chart-drawer'), {ssr: false})

/* Hooks */
import {notifyMe} from '../../integrations/notifyme/hooks/use-notify-me'
import {useHeaderContext} from '../../contexts/header-context'
import {useStickyState} from '../../contexts/sticky-context'
import useGiftCard from '../../hooks/use-gift-card'
import InformationAccordion from '../../pages/product-detail/partials/information-accordion'
import {GiftCallout} from './partials/giftCallout'
import {GiftCardCheckBalance} from '../../pages/product-detail/partials/giftCardCheckBalance'
import {sendClickEvent} from '../../utils/gtm-utils'
import {trackAddToWishlistEvent, trackRemoveFromWishlistEvent} from '../../utils/optimizely-exp-bag'
import {AuthModal, useAuthModal} from '../../hooks/use-auth-modal'

// MRT multi site
import {getSiteEnvVarValue} from '../../utils/site-config'
import {FEW_ITEMS_LEFT_QUANTITY_PREF_IDS} from '../../utils/fomo-messaging'
import {useSitePreferences} from '../../hooks/use-site-preferences'
import {resolveGDIStyles, GDI_TEXT_BLOCK_FIELD_MAP} from '../optimizely/gdi/utils/gdi-styles'

// partials
import ProductViewHeader from './partials/ProductViewHeader'
import VariationSelector from './partials/VariationSelector'
import ActionButtons from './partials/ActionButtons'

const messages = defineMessages({
    notifyMeError: {
        defaultMessage: 'Failed to submit data',
        id: 'pdp_notify_me_general.error'
    },
    emailValidationError: {
        defaultMessage: 'Please enter a valid email address',
        id: 'email.validation.error'
    }
})

/**
 * Render a product detail view that includes name, image gallery, price,
 * variant selections, action buttons
 */

const ProductView = forwardRef(
    (
        {
            product,
            category,
            showFullLink = false,
            imageSize = 'md',
            isWishlistLoading = false,
            addToCart,
            updateCart,
            addToWishlist,
            updateWishlist,
            onFindInStoreOpen,
            onLongDescriptionOpen,
            optimizelyContent,
            isOptimizelyLoading,
            isProductLoading,
            isPDP = false,
            isMasterProductChanging = false,
            isProductPartOfSet = false,
            isProductPartOfBundle = false,
            childOfBundleQuantity = 0,
            basket,
            childProductOrderability,
            setChildProductOrderability,
            isBasketLoading = false,
            isAvailabilityPending = false,
            onVariantSelected = () => {},
            validateOrderability = (variant, quantity, stockLevel) =>
                !isProductLoading && variant?.orderable && quantity > 0 && quantity <= stockLevel,
            showImageGallery = true,
            setSelectedBundleQuantity = () => {},
            selectedBundleParentQuantity = 1,
            isInWishlist = false,
            onReviewsClick,
            variantStockQuantity = undefined
        },
        ref
    ) => {
        const config = getConfig()
        const {site, locale: currentLocale} = useMultiSite()
        const siteId = site?.id
        const {
            data: fewItemsLeftQuantityPrefs,
            isLoading: fewItemsLeftQuantityPrefsLoading
        } = useSitePreferences(FEW_ITEMS_LEFT_QUANTITY_PREF_IDS)
        const notifyMeEnabled = getSiteEnvVarValue(config.app, 'NOTIFY_ME_ENABLED', siteId) === 'true'
        const styles = useMultiStyleConfig('ProductView', {isPDP})

        const [isFewItemsLeft, setIsFewItemsLeft] = useState(false)
        const [fewItemsLeftQuantity, setFewItemsLeftQuantity] = useState(null)
        const [isFewItemsLeftMessagePending, setIsFewItemsLeftMessagePending] = useState(false)
        const [isNotifyMeModalOpen, setIsNotifyMeModalOpen] = useState(false)
        const [notifyMeSuccessContent, setNotifyMeSuccessContent] = useState(false)
        const [notifyMeError, setNotifyMeError] = useState(null)
        const {isLoading: isNotifyMeLoading, submitNotifyMe} = notifyMe()
        const {data: customer} = useCurrentCustomer()
        const authModal = useAuthModal()
        const {currency: activeCurrency} = useCurrency()
        const navigate = useNavigation()
        const intl = useIntl()
        const location = useLocation()
        const {
            isOpen: isAddToCartModalOpen,
            onOpen: onAddToCartModalOpen,
            onClose: onAddToCartModalClose,
            onAutoClose: onAddToCartModalAutoClose
        } = useMiniCartContext()
        const {openConfirmation} = useAddToCartConfirmation()
        const [showOptionsMessage, toggleShowOptionsMessage] = useState(false)
        const [addToCartError, setAddToCartError] = useState('')
        const [isAddToCartBlocked, setIsAddToCartBlocked] = useState(false)
        const {
            showLoading,
            showInventoryMessage,
            inventoryMessage,
            quantity,
            minOrderQuantity,
            setQuantity,
            variant,
            variationParams,
            variationAttributes,
            stockLevel,
            stepQuantity,
            isOutOfStock,
            unfulfillable
        } = useDerivedProduct(product, isProductPartOfSet, isProductPartOfBundle)
        const isNotifyMe = useMemo(() => {
            if (!notifyMeEnabled) {
                return false
            }

            const lengthVariation = variationAttributes?.find(
                (variation) => variation.id === 'length'
            )
            const sizeVariation = variationAttributes?.find((variation) => variation.id === 'size')
            const selectedSize = sizeVariation?.selectedValue?.value
            const selectedLength = lengthVariation?.selectedValue?.value
            const hasLengthVariation = lengthVariation?.values?.length > 0

            if (variant && selectedSize && !hasLengthVariation) {
                const foundInArray = product?.c_notifyMeArray?.find(
                    ({size}) => size === selectedSize
                )
                return foundInArray?.size === selectedSize && !variant?.orderable
            }

            if (variant && hasLengthVariation && selectedSize && selectedLength) {
                const notifyMeSelectedVariation = product?.c_notifyMeArray?.find(
                    ({size, length}) => size === selectedSize && length === selectedLength
                )
                return (
                    notifyMeSelectedVariation?.size === selectedSize &&
                    notifyMeSelectedVariation?.length === selectedLength &&
                    !variant?.orderable
                )
            }

            return false
        }, [notifyMeEnabled, variationAttributes, variant, product])
        const priceData = useMemo(() => {
            if (variant?.productId && product?.variants) {
                const selectedVariant = product.variants.find(
                    (v) => v.productId === variant.productId
                )
                if (selectedVariant) {
                    const variantWithPromotions = {
                        ...selectedVariant,
                        productPromotions:
                            selectedVariant.productPromotions || product?.productPromotions,
                        c_newArrivalDate: selectedVariant?.c_newArrivalDate || product?.c_newArrivalDate
                    }
                    return getPriceData(variantWithPromotions, {quantity})
                }
            }
            return getPriceData(product, {quantity})
        }, [product, quantity, variant?.productId])
        const canAddToWishlist = !isProductLoading
        const isProductASet = product?.type.set
        const isProductABundle = product?.type.bundle
        const errorContainerRef = useRef(null)
        const hasScrolledToErrorRef = useRef(false)
        const isDesktop = useBreakpointValue({base: false, lg: true})
        const {headerHeight} = useHeaderContext()
        const {ref: addToCartRef, isInView} = useInView({
            rootMargin: `-${headerHeight}px 0px 100% 0px`
        })
        const {setPdpStickyButtonActive} = useStickyState()
        const {showCustomToast} = useCustomToast()
        const productDetailsRef = useRef()
        const giftCardFormContainerRef = useRef(null)
        const fitValue = product?.c_bvHowDoesItFitAverageRating
        const hasBazaarVoiceReviews = Number(product?.c_bvReviewCount || 0) > 0
        const [isShopSimilarOpen, setIsShopSimilarOpen] = useState(false)

        const MAX_PER_PRODUCT = product?.c_productMaxOrderQty || 100
        const productInBasket = basket?.productItems?.find((item) => item.productId === product?.id)
        const quantityInBasket = productInBasket?.quantity || 0
        const hasReachedMaxQuantity = quantityInBasket >= MAX_PER_PRODUCT
        const hasReachedATS =
            product?.inventory?.ats != null && productInBasket?.quantity === product?.inventory?.ats

        // Gift card hook - extracts all gift card related state and logic
        const {
            isGiftPage,
            giftCardFormRef,
            giftCardOrderableState,
            isCheckBalanceOpen,
            handleCheckBalanceOpen,
            handleCheckBalanceClose,
            validateGiftCardForm,
            getGiftCardData,
            resetGiftCardForm,
            getGiftCardErrorMessage,
            hasGiftCardFormErrors
        } = useGiftCard(product, {
            variant,
            variationAttributes,
            isOutOfStock,
            showInventoryMessage
        })

        const allVariationsSelected = useMemo(
            () => hasAllValidVariationSelections(variationAttributes),
            [variationAttributes]
        )

        const {disableButton, customInventoryMessage} = useMemo(() => {
            const hasAvailabilityData = product?.inventory != null
            let currentInventoryMsg = ''
            const hasUnselectedVariations = variationAttributes?.some(
                (attr) => !isVariationAttributeSelectionValid(attr)
            )

            let shouldDisableButton = isGiftPage
                ? giftCardOrderableState.isGiftCardDisabled
                : (() => {
                      if (hasUnselectedVariations) return false
                      if (allVariationsSelected && !variant) return true
                      if (variant && !variant.orderable) return true
                      if (variant && variant.orderable) return false
                      return showInventoryMessage || isOutOfStock
                  })()

            if (hasReachedMaxQuantity || hasReachedATS) {
                shouldDisableButton = true
                currentInventoryMsg = intl.formatMessage({
                    defaultMessage: 'Max quantity in cart',
                    id: 'product_view.error.max_quantity_reached'
                })
            }

            if (
                !shouldDisableButton &&
                (isProductASet || isProductABundle) &&
                childProductOrderability
            ) {
                const unavailableChildProductKey = Object.keys(childProductOrderability).find(
                    (key) => {
                        return childProductOrderability[key].showInventoryMessage
                    }
                )
                shouldDisableButton = !!unavailableChildProductKey
                if (unavailableChildProductKey) {
                    const unavailableChildProduct =
                        childProductOrderability[unavailableChildProductKey]
                    if (unavailableChildProduct.unfulfillable) {
                        currentInventoryMsg = intl.formatMessage(
                            {
                                defaultMessage: 'Only {stockLevel} left for {productName}!',
                                id: 'use_product.message.inventory_remaining_for_product'
                            },
                            {
                                stockLevel: unavailableChildProduct.stockLevel,
                                productName: unavailableChildProduct.productName
                            }
                        )
                    }
                    if (unavailableChildProduct.isOutOfStock) {
                        currentInventoryMsg = intl.formatMessage(
                            {
                                defaultMessage: 'Out of stock for {productName}',
                                id: 'use_product.message.out_of_stock_for_product'
                            },
                            {productName: unavailableChildProduct.productName}
                        )
                    }
                }
            }
            return {
                disableButton: shouldDisableButton,
                customInventoryMessage: hasAvailabilityData ? currentInventoryMsg : ''
            }
        }, [
            showInventoryMessage,
            childProductOrderability,
            hasReachedMaxQuantity,
            hasReachedATS,
            variant,
            variationAttributes,
            allVariationsSelected,
            product,
            isOutOfStock,
            isGiftPage,
            giftCardOrderableState
        ])

        const validateAndShowError = async (opts = {}) => {
            const {scrollErrorIntoView = true} = opts
            const hasValidSelection = validateOrderability(variant, quantity, stockLevel)
            let showError = !isProductASet && !isProductABundle && !hasValidSelection
            const hasMissingVariationSelection = (variationAttributes || []).some(
                (attr) => !attr.selectedValue?.value
            )

            const giftCardFormValid = await validateGiftCardForm()
            const scrollToError = showError && scrollErrorIntoView && !hasScrolledToErrorRef.current

            toggleShowOptionsMessage(showError)

            // On mobile, scroll to the first invalid gift card input so the user can see it
            if (!giftCardFormValid && scrollErrorIntoView && !isDesktop && giftCardFormContainerRef.current) {
                window.requestAnimationFrame(() => {
                    const container = giftCardFormContainerRef.current
                    if (!container) return
                    const firstError = container.querySelector(
                        'input:not([type="hidden"])[aria-invalid="true"], textarea[aria-invalid="true"], select[aria-invalid="true"]'
                    )
                    const target = firstError || container
                    target.scrollIntoView({behavior: 'smooth', block: 'center'})
                })
            }

            if (scrollToError) {
                if (hasMissingVariationSelection && productDetailsRef.current) {
                    productDetailsRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    })
                } else if (errorContainerRef.current) {
                    errorContainerRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    })
                }
                hasScrolledToErrorRef.current = true
            }

            return hasValidSelection && giftCardFormValid
        }

        const handleNotifyMeCloseModal = () => {
            setIsNotifyMeModalOpen(false)
            setNotifyMeSuccessContent(false)
        }

        const handleShopSimilarOpenModal = () => {
            const trackedProduct = variant
                ? {
                      ...product,
                      ...variant,
                      productName:
                          product?.productName ||
                          product?.name ||
                          variant?.productName ||
                          variant?.name,
                      name: product?.name || product?.productName || variant?.name || variant?.productName
                  }
                : product
            sendClickEvent('shop_similar - pdp', trackedProduct)
            setIsShopSimilarOpen(true)
        }

        const handleShopSimilarCloseModal = () => {
            setIsShopSimilarOpen(false)
        }

        const handleEarlyAccess = (event) => {
            event?.preventDefault?.()
            authModal.onOpen()
        }

        const handleCartItem = async (scrollErrorIntoView = true) => {
            const hasValidSelection = await validateAndShowError({scrollErrorIntoView})
            if (!hasValidSelection) return null
            if (!addToCart && !updateCart) return null

            const giftCardData = await getGiftCardData()
            setAddToCartError('')
            setIsAddToCartBlocked(false)

            if (updateCart) {
                await updateCart(variant || product, quantity, giftCardData)
                resetGiftCardForm()
                return
            }

            try {
                const itemsAdded = await addToCart(variant, quantity, giftCardData)
                if (itemsAdded) {
                    resetGiftCardForm()
                    openConfirmation({product, variant, quantity})
                } else {
                    // Maximum qty error
                    setAddToCartError(
                        intl.formatMessage({
                            id: 'product_view.error.not_added_to_cart',
                            defaultMessage: 'Maximum quantity has been reached'
                        })
                    )
                    setIsAddToCartBlocked(true)
                }
            } catch (e) {
                showCustomToast({
                    message: intl.formatMessage(API_ERROR_MESSAGE)
                })
            }
        }

        const handleWishlistItem = async () => {
            if (!updateWishlist && !addToWishlist) return null
            if (updateWishlist) {
                updateWishlist(product, variant, quantity)
                return
            }
            addToWishlist(product, variant, quantity)
            if (isInWishlist) {
                trackRemoveFromWishlistEvent({
                    sku: variant.id,
                    value: product.price,
                    variationGroupId: product.id,
                    category: product.primaryCategoryId,
                    source: 'product-detail'
                })
            } else {
                trackAddToWishlistEvent({
                    sku: variant.id,
                    value: product.price,
                    variationGroupId: product.id,
                    category: product.primaryCategoryId,
                    source: 'product-detail'
                })
            }
        }

        const handleNotifyMe = () => {
            // Variants often lack productName/name — keep variant ids/sku but name from product.
            const trackedProduct = variant
                ? {
                      ...product,
                      ...variant,
                      productName:
                          product?.productName ||
                          product?.name ||
                          variant?.productName ||
                          variant?.name,
                      name: product?.name || product?.productName || variant?.name || variant?.productName
                  }
                : product
            sendClickEvent('notify_me - pdp', trackedProduct)
            setNotifyMeSuccessContent(false)
            setIsNotifyMeModalOpen(true)
        }

        const buttonText = {
            update: intl.formatMessage({
                defaultMessage: 'Update',
                id: 'product_view.button.update'
            }),
            addToCart: intl.formatMessage({
                defaultMessage: 'Add to Bag',
                id: 'product_view.button.add_to_cart'
            }),
            addSetToCart: intl.formatMessage({
                defaultMessage: 'Add Set to Bag',
                id: 'product_view.button.add_set_to_cart'
            }),
            addBundleToCart: intl.formatMessage({
                defaultMessage: 'Add Bundle to Bag',
                id: 'product_view.button.add_bundle_to_cart'
            }),
            addToWishlist: intl.formatMessage({
                defaultMessage: 'Add to Wishlist',
                id: 'product_view.button.add_to_wishlist'
            })
        }

        const renderActionButtons = (opts = {}) => {
            const {scrollErrorIntoView = true} = opts
            const isCtaResolving =
                isProductLoading ||
                isMasterProductChanging ||
                showLoading ||
                isAvailabilityPending ||
                (variant && !Object.prototype.hasOwnProperty.call(variant, 'orderable'))
            const shouldShowNotifyMe = isNotifyMe
            const shouldShowShopSimilar =
                getSiteEnvVarValue(config.app, 'VICKY_ENABLED', siteId) === 'true' &&
                !isNotifyMe &&
                ((variant &&
                    Object.prototype.hasOwnProperty.call(variant, 'orderable') &&
                    !variant.orderable) ||
                    (allVariationsSelected && !variant))
            const isEarlyAccessProduct = product?.c_isEarlyAccess ?? false
            const isGuestUser = !customer?.isRegistered
            const shouldShowEarlyAccess = isGuestUser && isEarlyAccessProduct
            const shouldShowAddToCart = !isNotifyMe && !shouldShowShopSimilar && !shouldShowEarlyAccess
            const ctaLoading = isCtaResolving || isBasketLoading
            const notifyMeButtonExpanded =
                optimizelyContent?.item?.NotifyMeButtonText?.[0]?.ContentLink?.Expanded
            const notifyMeButtonText = notifyMeButtonExpanded?.TextForWeb
                ? notifyMeButtonExpanded.TextForWeb.replace(/<[^>]*>/g, '').trim()
                : ''
            const {styles: notifyMeButtonResolvedStyles} = resolveGDIStyles(
                notifyMeButtonExpanded?.GDITextBlockStylesPerViewType,
                GDI_TEXT_BLOCK_FIELD_MAP
            )
            const notifyMeButtonSx =
                Object.keys(notifyMeButtonResolvedStyles).length > 0
                    ? notifyMeButtonResolvedStyles
                    : null

            return (
                <ActionButtons
                    shouldShowShopSimilar={shouldShowShopSimilar}
                    shouldShowNotifyMe={shouldShowNotifyMe}
                    shouldShowAddToCart={shouldShowAddToCart}
                    shouldShowEarlyAccess={shouldShowEarlyAccess}
                    ctaLoading={ctaLoading}
                    isNotifyMeLoading={isNotifyMeLoading}
                    disableButton={disableButton || isAddToCartBlocked}
                    addToCart={addToCart}
                    updateCart={updateCart}
                    addToWishlist={addToWishlist}
                    updateWishlist={updateWishlist}
                    isProductPartOfBundle={isProductPartOfBundle}
                    isGiftPage={isGiftPage}
                    isProductASet={isProductASet}
                    isProductABundle={isProductABundle}
                    canAddToWishlist={canAddToWishlist}
                    isWishlistLoading={isWishlistLoading}
                    isInWishlist={isInWishlist}
                    onCartItem={() => handleCartItem(scrollErrorIntoView)}
                    onNotifyMe={handleNotifyMe}
                    onShopSimilar={handleShopSimilarOpenModal}
                    onEarlyAccess={handleEarlyAccess}
                    onWishlistItem={handleWishlistItem}
                    wishlistProductId={variant?.productId || product?.id}
                    variantId={variant?.productId || product?.id}
                    variationAttributes={variationAttributes}
                    setApplePayError={setAddToCartError}
                    buttonText={buttonText}
                    notifyMeButtonText={notifyMeButtonText}
                    notifyMeButtonSx={notifyMeButtonSx}
                    isPDP={isPDP}
                />
            )
        }

        // Bind the reference with our `scope` that includes the internal validate function for this component.
        if (typeof ref === 'function') {
            ref = ref.bind({validateOrderability: validateAndShowError})
        }

        // Set the quantity of bundle child in a product bundle to ensure availability messages appear
        if (
            isProductPartOfBundle &&
            quantity != selectedBundleParentQuantity * childOfBundleQuantity
        ) {
            setQuantity(selectedBundleParentQuantity * childOfBundleQuantity)
        }

        const openFindInStoreModal = () => {
            onFindInStoreOpen()
        }

        const openLongDescriptionModal = () => {
            onLongDescriptionOpen?.()
        }

        useEffect(() => {
            setPdpStickyButtonActive(!isInView)
        }, [isInView])

        useEffect(() => {
            if (isAddToCartModalOpen) {
                onAddToCartModalClose()
            }
        }, [location.pathname])

        useEffect(() => {
            if (
                !isProductASet &&
                !isProductABundle &&
                validateOrderability(variant, quantity, stockLevel)
            ) {
                toggleShowOptionsMessage(false)
            }
        }, [variationParams])

        useEffect(() => {
            if (addToCartError || isAddToCartBlocked) {
                setAddToCartError('')
                setIsAddToCartBlocked(false)
            }
        }, [variant?.productId, quantity])

        useEffect(() => {
            if (!showOptionsMessage) {
                hasScrolledToErrorRef.current = false
            }
        }, [showOptionsMessage])

        useEffect(() => {
            if (variant) {
                onVariantSelected(product, variant, quantity)
            }
        }, [variant?.productId, quantity])

        useEffect(() => {
            const selectedSize = variationAttributes?.find((attr) => attr.id === 'size')
                ?.selectedValue?.value
            const selectedLength = variationAttributes?.find((attr) => attr.id === 'length')
                ?.selectedValue?.value
            const hasLengthVariation = product?.variationAttributes?.find(
                (attr) => attr.id === 'length'
            )

            const qtyEnabled = fewItemsLeftQuantityPrefs?.fewItemsLeftQuantityEnabled
            const max = +fewItemsLeftQuantityPrefs?.fewItemsLeftQuantityThreshold || 9
            const stockLevelResolved = variantStockQuantity != null
            const level = stockLevelResolved ? +variantStockQuantity : NaN

            let eligible = false
            if (selectedSize) {
                const sizeFewItemsLeft =
                    hasLengthVariation && selectedLength
                        ? product?.c_fewItemsLeftArray?.find(
                              ({size, length}) => size === selectedSize && length === selectedLength
                          )
                        : product?.c_fewItemsLeftArray?.find(({size}) => size === selectedSize)
                eligible = !!sizeFewItemsLeft
            } else {
                eligible = !!product?.c_fewItemsLeft && !product?.c_isEGiftCard
            }

            if (!eligible) {
                setIsFewItemsLeft(false)
                setFewItemsLeftQuantity(null)
                setIsFewItemsLeftMessagePending(false)
                return
            }

            if (fewItemsLeftQuantityPrefsLoading || (qtyEnabled && !stockLevelResolved)) {
                setIsFewItemsLeft(false)
                setFewItemsLeftQuantity(null)
                setIsFewItemsLeftMessagePending(true)
                return
            }

            setIsFewItemsLeftMessagePending(false)
            setIsFewItemsLeft(true)
            setFewItemsLeftQuantity(
                qtyEnabled && level > 0 && level <= max ? level : null
            )
        }, [product, variationAttributes, variant?.productId, variantStockQuantity, fewItemsLeftQuantityPrefs, fewItemsLeftQuantityPrefsLoading])

        useEffect(() => {
            if (isProductPartOfBundle || isProductPartOfSet) {
                const key = product.itemId ?? product.id
                setChildProductOrderability((previousState) => ({
                    ...previousState,
                    [key]: {
                        showInventoryMessage,
                        isOutOfStock,
                        unfulfillable,
                        stockLevel,
                        productName: product?.name
                    }
                }))
            }
        }, [showInventoryMessage, inventoryMessage])

        const {missingAttributeMessages, giftCardError} = useMemo(() => {
            const msgs = {}
            const giftCardMessage = getGiftCardErrorMessage(variationAttributes)

            ;(variationAttributes || []).forEach((attr) => {
                if (!attr.selectedValue?.value) {
                    msgs[attr.id] = intl.formatMessage(
                        {
                            defaultMessage: 'Please select a {attribute}',
                            id: 'product_view.error.missing_single_attribute'
                        },
                        {attribute: attr.name.toLowerCase()}
                    )
                }
            })

            return {
                missingAttributeMessages: msgs,
                giftCardError: giftCardMessage
            }
        }, [variationAttributes, intl, getGiftCardErrorMessage])

        return (
            <Flex direction={'column'} data-testid="product-view" ref={ref}>
                {!isGiftPage && <KlarnaScript />}

                {isDesktop && (
                    <Skeleton isLoaded={category} minWidth={64}>
                        {/* Breadcrumb */}
                        <Breadcrumb categories={category} variant="pdp" />
                    </Skeleton>
                )}
                <Flex direction={['column', 'column', 'column', 'row']}>
                    {showImageGallery && (
                        <Box
                            {...styles.imageGalleryBox}
                            {...(isGiftPage && {
                                position: {base: 'relative', lg: 'sticky'},
                                top: {base: 'auto', lg: '0'},
                                height: {base: 'auto', lg: 'calc(100vh - 80px)'},
                                alignSelf: 'flex-start'
                            })}
                        >
                            {product && (!isProductLoading || isGiftPage) ? (
                                <>
                                    <ImageGallery
                                        size={imageSize}
                                        imageGroups={product.imageGroups}
                                        selectedVariationAttributes={variationParams}
                                        badges={product?.c_badges}
                                        isGiftPage={isGiftPage}
                                        videoSequencePositions={
                                            (() => {
                                                const brand = product?.brand?.toLowerCase()
                                                const isGarage = brand === 'garage'
                                                const isDynamite = brand === 'dynamite'
                                                const garageEnabled = getSiteEnvVarValue(config.app, 'ENABLE_PRODUCT_VIDEO_GARAGE', siteId) === 'true'
                                                const dynamiteEnabled = getSiteEnvVarValue(config.app, 'ENABLE_PRODUCT_VIDEO_DYNAMITE', siteId) === 'true'
                                                if ((isGarage && garageEnabled) || (isDynamite && dynamiteEnabled)) {
                                                    return product?.c_videoSequencePositions || null
                                                }
                                                return null
                                            })()
                                        }
                                    />
                                    <HideOnMobile>
                                        {showFullLink && product && (
                                            <Link
                                                to={`/product/${product.master.masterId}`}
                                                color="blue.600"
                                            >
                                                <FormattedMessage
                                                    id="product_view.link.full_details"
                                                    defaultMessage="See full details"
                                                />
                                            </Link>
                                        )}
                                    </HideOnMobile>
                                </>
                            ) : (
                                <ImageGallerySkeleton />
                            )}
                        </Box>
                    )}

                    {/* Variations & Quantity Selector & CTA buttons */}
                    <VStack
                        {...styles.detailsStack}
                        {...(!isGiftPage && {
                            position: 'sticky',
                            top: '72px'
                        })}
                        ref={productDetailsRef}
                    >
                        <Box className="header-wrapper">
                            <ProductViewHeader
                                name={product?.name}
                                product={product}
                                priceData={priceData}
                                currency={product?.currency || activeCurrency}
                                category={category}
                                isProductPartOfBundle={isProductPartOfBundle}
                                onReviewsClick={onReviewsClick}
                                isGiftPage={isGiftPage}
                                isLoading={isMasterProductChanging}
                            />
                        </Box>
                        <VStack align="stretch" spacing={isGiftPage ? {base: 6, lg: 8} : 0}>
                            {isProductPartOfBundle && (
                                <Box>
                                    <Box {...styles.qtylabel} aria-label="price">
                                        <label>
                                            {intl.formatMessage({
                                                defaultMessage: 'Quantity',
                                                id: 'product_view.label.quantity'
                                            })}
                                            : {childOfBundleQuantity}
                                        </label>
                                    </Box>
                                </Box>
                            )}

                            {/* Options */}
                            {isPDP && (
                                <h2 data-testid="pdp-text-color-name" style={{display: 'none'}}>
                                    {variationParams?.color
                                        ? product?.variationAttributes?.find(attr => attr.id === 'color')?.values?.find(v => v.value === variationParams?.color)?.name || 'Color'
                                        : 'Color'}
                                </h2>
                            )}
                            <VariationSelector
                                variationAttributes={variationAttributes}
                                showLoading={showLoading || isAvailabilityPending}
                                isGiftPage={isGiftPage}
                                currency={product?.currency || activeCurrency}
                                showOptionsMessage={showOptionsMessage}
                                missingAttributeMessages={missingAttributeMessages}
                                customInventoryMessage={customInventoryMessage}
                                product={product}
                                notifyMeEnabled={notifyMeEnabled}
                                variationParams={variationParams}
                                optimizelyContent={optimizelyContent}
                                sizeChartId={product?.c_sizeChartWeb}
                                category={category}
                                onReviewsClick={onReviewsClick}
                                fitValue={fitValue}
                                hasBazaarVoiceReviews={hasBazaarVoiceReviews}
                                isFewItemsLeft={isFewItemsLeft}
                                isFewItemsLeftMessagePending={isFewItemsLeftMessagePending}
                                fewItemsLeftQuantity={fewItemsLeftQuantity}
                                isNotifyMe={isNotifyMe}
                            />

                            {/* Gift Card Form - Show for eGift Cards */}
                            <Box ref={giftCardFormContainerRef} className="gift-card-form">
                                {/* Gift Card Form */}
                                <GiftCardForm
                                    ref={giftCardFormRef}
                                    isVisible={product?.c_isEGiftCard}
                                    headingIndex={(variationAttributes?.length || 0) + 1}
                                />

                                {/* Check Balance */}
                                <GiftCardCheckBalance
                                    isOpen={isCheckBalanceOpen}
                                    onClose={handleCheckBalanceClose}
                                />

                                {/* Form error */}
                                <Box ref={errorContainerRef}>
                                    {!showLoading && addToCartError && (
                                        <Box
                                            {...styles.maxQtyErrorText}
                                            marginTop={4}
                                            role="alert"
                                        >
                                            {addToCartError}
                                        </Box>
                                    )}
                                    {!showLoading && showOptionsMessage && giftCardError && (
                                        <Box
                                            {...styles.giftCardErrorText}
                                            marginTop={isGiftPage ? 4 : 0}
                                        >
                                            {giftCardError}
                                        </Box>
                                    )}
                                </Box>
                                <HideOnDesktop>
                                    {showFullLink && product && (
                                        <Link
                                            to={`/product/${product.master.masterId}`}
                                            color="blue.600"
                                        >
                                            <FormattedMessage
                                                id="product_view.link.full_details"
                                                defaultMessage="See full details"
                                            />
                                        </Link>
                                    )}
                                </HideOnDesktop>
                                {isProductASet && <p>{product?.shortDescription}</p>}
                            </Box>
                        </VStack>

                        <Box>
                            {/* Add to Cart */}
                            <Box ref={addToCartRef} {...styles.addToCartBox}>
                                {renderActionButtons({scrollErrorIntoView: true})}
                            </Box>

                            {!isGiftPage && (
                                <Box {...styles.klarnaPlacement}>
                                    <klarna-placement
                                        data-key="credit-promotion-auto-size"
                                        data-locale={currentLocale?.id || 'en-GB'}
                                        data-purchase-amount={
                                            Math.round(
                                                priceData?.currentPrice *
                                                    (priceData?.quantity ?? 1) *
                                                    100
                                            ) || 0
                                        }
                                    />
                                </Box>
                            )}
                        </Box>

                        {/* Gift Note section */}
                        {isGiftPage && <GiftCallout />}

                        {/* Infomation accordion */}
                        <InformationAccordion
                            product={product}
                            variant={variant}
                            openFindInStoreModal={openFindInStoreModal}
                            openLongDescriptionModal={openLongDescriptionModal}
                            optimizelyContent={optimizelyContent}
                            isOptimizelyLoading={isOptimizelyLoading}
                            isGiftPage={isGiftPage}
                            checkBalanceOnOpen={handleCheckBalanceOpen}
                            pdpCmsContext={{
                                primaryCategoryId: product?.primaryCategoryId,
                                parentCategoryTree: Array.isArray(category) ? category : [],
                                productId: product?.id,
                                masterProductId: product?.master?.masterId,
                                variantProductId: variant?.productId
                            }}
                        />
                    </VStack>
                </Flex>

                {!isInView && (
                    <StickyButton
                        name={product?.name}
                        priceData={priceData}
                        currency={product?.currency || activeCurrency}
                        isProductPartOfBundle={isProductPartOfBundle}
                        renderActionButtons={renderActionButtons}
                        productDetailsRef={productDetailsRef}
                        giftCardFormContainerRef={giftCardFormContainerRef}
                        hasGiftCardFormErrors={hasGiftCardFormErrors}
                        variationAttributes={variationAttributes}
                        showLoading={showLoading}
                        intl={intl}
                        actualFitComponent={
                            fitValue && hasBazaarVoiceReviews ? (
                                <ActualFitMessage fit={fitValue} onClick={onReviewsClick} />
                            ) : null
                        }
                        sizeChartComponent={
                            <SizeChartDrawer
                                optimizelyContent={optimizelyContent}
                                sizeChartId={product?.c_sizeChartWeb}
                            />
                        }
                    />
                )}

                {isNotifyMe && (
                    <NotifyMeModal
                        key="notify-me-modal"
                        isOpen={isNotifyMeModalOpen}
                        onClose={handleNotifyMeCloseModal}
                        variant={variant}
                        product={product}
                        showSuccessContent={notifyMeSuccessContent}
                        notifyMeDrawerTitle={optimizelyContent?.item?.NotifyMeDrawerTitle?.[0]?.ContentLink?.Expanded}
                        notifyMeRecommendation={optimizelyContent?.item?.NotifyMeRecommendation}
                    />
                )}

                <ShopSimilarModal
                    key="shop-similar-modal"
                    isOpen={isShopSimilarOpen}
                    onClose={handleShopSimilarCloseModal}
                    product={product}
                    shopSimilarDrawerTitle={optimizelyContent?.item?.ShopSimilarDrawerTitle?.[0]?.ContentLink?.Expanded}
                    shopSimilarRecommendation={optimizelyContent?.item?.ShopSimilarRecommendation}
                />
                <AuthModal {...authModal} />
            </Flex>
        )
    }
)

ProductView.displayName = 'ProductView'

ProductView.propTypes = {
    product: PropTypes.object,
    isProductPartOfSet: PropTypes.bool,
    isProductPartOfBundle: PropTypes.bool,
    childOfBundleQuantity: PropTypes.number,
    category: PropTypes.array,
    isProductLoading: PropTypes.bool,
    isMasterProductChanging: PropTypes.bool,
    isBasketLoading: PropTypes.bool,
    isWishlistLoading: PropTypes.bool,
    isAvailabilityPending: PropTypes.bool,
    addToCart: PropTypes.func,
    addToWishlist: PropTypes.func,
    updateCart: PropTypes.func,
    updateWishlist: PropTypes.func,
    showFullLink: PropTypes.bool,
    imageSize: PropTypes.oneOf(['sm', 'md']),
    childProductOrderability: PropTypes.object,
    setChildProductOrderability: PropTypes.func,
    onVariantSelected: PropTypes.func,
    validateOrderability: PropTypes.func,
    showImageGallery: PropTypes.bool,
    setSelectedBundleQuantity: PropTypes.func,
    selectedBundleParentQuantity: PropTypes.number,
    onFindInStoreOpen: PropTypes.func,
    optimizelyContent: PropTypes.object,
    isOptimizelyLoading: PropTypes.bool,
    isInWishlist: PropTypes.bool,
    onReviewsClick: PropTypes.func,
    basket: PropTypes.object,
    variantStockQuantity: PropTypes.number
}

export default ProductView
